import React, { useState, FC, useMemo, useEffect } from 'react';
import { Stack, Box, Typography, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';
import Helmet from 'react-helmet';
import HbtBreadcrumbComponent from 'Feature/V2/HbtBreadcrumb/components/HbtBreadcrumbComponent';
import HbtMasterPoolPricingScenariosFlyout from './components/HbtMasterPoolAddModifyPricingScenariosFlyout';
import {
  checkForFinalizeRules,
  getLoanStatusLabel,
  getMasterPoolAccountStatusCode,
  getMasterPoolCmhcAccountNumber,
  getRiskedOrOffered
} from '../../../HbtMasterPoolDetails/utils/masterPoolDetailsUtils';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { setSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/setSearchParams';
import LoanStatusPill from 'Feature/V2/HbtMasterPool/shared/LoanStatusPill';
import { loanStatusMapping } from '../../../HbtMasterPoolTable/utils/LoanStatusMapping';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  CmhcIcon,
  CmhcIconSize,
  MRTDataTable,
  NotificationBanner,
  ToastNotification,
  ToastNotificationVariant
} from '@cmhc-schl/hearth-ui';
import { useMRTCustomTranslation } from 'Feature/V2/Shared_Portfolio/Hooks/useMRTCustomTransaltion';
import i18n from 'i18next';
import { createColumns } from './columns/TableColumns';
import { PricingScenarioData, RiskResult, ScenarioData, ToastNotificationConfig } from './types';
import { RowHeader } from './RowHeader';
import { getScenarioType, TransposeData } from './utils/TransposedData';
import { useAuthenticationContext } from 'Foundation/Authentication';
import { useQuery } from '@tanstack/react-query';
import {
  getPricingScenario,
  HTTP_STATUS_CONFLICT
} from './api/HbtMasterPoolLoansInventoryDetailService';
import moment from 'moment';
import { NotificationBannerType } from 'Feature/V2/HbtPIRequest/components/HbtPIRequestPage/types';
import { PricingScenarios } from './components/PricingScenarios';
import { PricingScenarioModalData } from './utils/PricingScenariosModalData';
import { useLockAndUnlockScenario, usePatchOfferScenario } from './api/hooks/useMutations';
import { PortfolioLoanStatusCode } from 'Feature/V2/HbtPortfolioManagement/components/HbtPortfolioManagementRequestOverview/api/types';
import { OfferTable } from './components/OfferTable/OfferTable';
import { IsLockedIndicator } from '../../../HbtMasterPoolDetails/utils/masterPoolConstants';
import { MasterPoolStatusEnum } from '../../../HbtMasterPoolTable/types';

const PINNED_COLUMN_BACKGROUND = '#f7f8f9';
const PINNED_ROW_HEADER_KEYS = new Set<string>([
  'approvedVolumes',
  'premiumSummary',
  'riskProfileSummary'
]);

const HbtMasterPoolPricingScenarios: FC = () => {
  const [isPricingScenarios, setIsPricingScenarios] = useState<boolean>(false);
  const [isLockAndUnlock, setIsLockAndUnlock] = useState<boolean>(false);
  const [risk, setRisk] = useState<RiskResult>();

  const [addScenarioToastOpen, setAddScenarioToastOpen] = useState<boolean>(false);
  const [modifyScenarioToastOpen, setModifyScenarioToastOpen] = useState<boolean>(false);
  const [openAddScenarioPricingScenarioFlyout, setAddScenarioOpenPricingScenarioFlyout] =
    useState<boolean>(false);
  const [openModifyScenarioPricingScenarioFlyout, setModifyScenarioOpenPricingScenarioFlyout] =
    useState<boolean>(false);

  const { t } = useTranslation('HbtMasterPool');
  const { t: ppricingScenarioLocales } = useTranslation('HbtMasterPoolPricingScenario');
  const masterPoolCmhcAccountNumber = getMasterPoolCmhcAccountNumber();
  const authenticationContext = useAuthenticationContext();
  const [selectedScenarioNumber, setSelectedScenarioNumber] = useState<number | undefined>(
    undefined
  );
  const [errorNotification, setErrorNotification] = useState(false);
  const [isOfferToastNotification, setIsOfferToastNotification] = useState(false);
  const [notificationBannerConfig, setNotificationBannerConfig] = useState<ToastNotificationConfig>(
    { header: '', subHeader: '' }
  );
  const [toastNotificationConfig, setToastNotificationConfig] = useState<ToastNotificationConfig>({
    header: '',
    subHeader: ''
  });

  const showWorkflowInProgressToast = () => {
    setToastNotificationConfig({
      header: ppricingScenarioLocales('workflow-Inprogress-Notification-Header'),
      subHeader: ppricingScenarioLocales('workflow-Inprogress-Notification-Body')
    });
    setIsOfferToastNotification(true);
  };

  const isWorkflowConflictError = (error: unknown) => {
    return (error as any)?.response?.status === HTTP_STATUS_CONFLICT;
  };

  const fetchData = async (): Promise<PricingScenarioData | null> => {
    const data = await getPricingScenario({
      authenticationContext,
      data: {
        cmhcAccountNumber: Number(masterPoolCmhcAccountNumber)
      }
    });
    return data;
  };

  const {
    data: pricingScenario,
    refetch: refetchPricingScenario,
    isFetching: isFetchingPricingScenario
  } = useQuery({
    queryKey: ['pricingScenario', masterPoolCmhcAccountNumber],
    queryFn: fetchData
  });

  const masterPoolAccountStatusCode = useMemo(
    () => pricingScenario?.masterPoolStatusCode ?? '',
    [pricingScenario]
  );

  const showFooter =
    masterPoolAccountStatusCode === MasterPoolStatusEnum.Offer ||
    masterPoolAccountStatusCode === MasterPoolStatusEnum.Lock;

  const handleFooterScenarioClick = async (risk: RiskResult) => {
    setRisk(risk);
    setIsLockAndUnlock(true);
  };

  const toggleAddScenarioPricingScenarioDrawer = (newOpen: boolean) => {
    setAddScenarioOpenPricingScenarioFlyout(newOpen);
    if (!newOpen && !!selectedScenarioNumber) setSelectedScenarioNumber(undefined);
  };

  const toggleModifyScenarioPricingScenarioDrawer = (newOpen: boolean) => {
    setModifyScenarioOpenPricingScenarioFlyout(newOpen);
    if (!newOpen && !!selectedScenarioNumber) setSelectedScenarioNumber(undefined);
  };

  const toggleAddScenarioToastNotification = (newOpen: boolean) => {
    setAddScenarioToastOpen(newOpen);
  };

  const toggleModifyScenarioToastNotification = (newOpen: boolean) => {
    setModifyScenarioToastOpen(newOpen);
  };

  const { mutateAsync } = usePatchOfferScenario();
  const { mutateAsync: lockUnlock } = useLockAndUnlockScenario();

  const formatString = 'YYYY-MM-DD-HH.mm.ss.SSSSSS';
  const indicator = Number(pricingScenario?.replacementPoolIndicator);
  const fileReceivedTimestamp = moment(pricingScenario?.fileReceivedTimestamp, formatString).format(
    'MMMM Do, YYYY'
  );
  const handleModifyScenarioFromHeader = (scenarioNumber: number) => {
    toggleModifyScenarioPricingScenarioDrawer(true);
    setSelectedScenarioNumber(scenarioNumber);
  };

  const status = getRiskedOrOffered(masterPoolAccountStatusCode);

  const isRisked = status === PortfolioLoanStatusCode.Risked;

  const columns = useMemo(() => {
    return createColumns({
      t,
      language: i18n.language,
      indicator,
      timeStamp: fileReceivedTimestamp ?? '',
      data: pricingScenario?.scenarios ?? [],
      handleFooterScenarioClick,
      showFooter,
      onModifyScenario: handleModifyScenarioFromHeader,
      pricingScenarioLocales: ppricingScenarioLocales,
      isRisked,
      masterPoolStatusCode: masterPoolAccountStatusCode
    });
  }, [
    t,
    i18n.language,
    indicator,
    fileReceivedTimestamp,
    pricingScenario?.scenarios,
    handleFooterScenarioClick,
    showFooter,
    handleModifyScenarioFromHeader,
    ppricingScenarioLocales,
    isRisked
  ]);

  const tableKey = useMemo(() => columns.map((c) => c.id).join('|'), [columns]);

  const transposedData = useMemo(() => {
    const scenarios = pricingScenario?.scenarios ?? [];
    const formattedTimestamp = fileReceivedTimestamp ?? '';
    const scenarioType = getScenarioType(scenarios, indicator, formattedTimestamp);
    return TransposeData(
      scenarios,
      RowHeader(scenarios, t, indicator, formattedTimestamp),
      scenarioType,
      indicator,
      formattedTimestamp
    );
  }, [pricingScenario?.scenarios, indicator, fileReceivedTimestamp, pricingScenario, t]);

  const pricingModalData = PricingScenarioModalData(columns, transposedData);
  const mrtCustomTranslation = useMRTCustomTranslation(transposedData.length);
  const { dataType, searchQuery } = getSearchParams(location.search);

  const params = Object.fromEntries(
    Object.entries(searchQuery).filter(([key]) => {
      if (key === 'masterPoolCmhcAccountNumber' && dataType !== 'Master Pool Number') {
        return false;
      }
      return true;
    })
  );
  const rowStyle = (rowDepth: number, p0: { ignoreBg: boolean }) => {
    if (rowDepth === 0) {
      return {
        backgroundColor: '#F7F8F9',
        borderBottom: '2px solid #B4BBC2'
      };
    }

    return {
      backgroundColor: '#fff'
    };
  };

  const searchParams =
    dataType && searchQuery ? setSearchParams({ dataType, query: { ...params } }) : '';

  const shouldSkipSearchPageFromBreadcrumb =
    dataType === 'Master Pool Number' || dataType === 'Sub Pool Number' || !dataType;
  const skippedHrefs = shouldSkipSearchPageFromBreadcrumb
    ? [`/pages/master-pool/search?${searchParams}`]
    : [];

  const offerScenarioConfiguration = async () => {
    try {
      const result = await mutateAsync({
        authenticationContext,
        masterPoolAccountNumber: Number(masterPoolCmhcAccountNumber)
      });
      if (result) {
        await refetchPricingScenario();
        const currentStatus = getRiskedOrOffered(pricingScenario?.masterPoolStatusCode ?? '');
        const isCurrentlyRisked = currentStatus === PortfolioLoanStatusCode.Risked;
        const newStatus = isCurrentlyRisked
          ? PortfolioLoanStatusCode.Offer
          : PortfolioLoanStatusCode.Risked;

        setToastNotificationConfig({
          header:
            newStatus !== PortfolioLoanStatusCode.Risked
              ? ppricingScenarioLocales('offer-toast-header')
              : ppricingScenarioLocales('removing-offer'),
          subHeader: ''
        });
        setIsOfferToastNotification(true);

        setIsPricingScenarios(false);

        return result;
      }
    } catch (error) {
      setErrorNotification(true);
      setNotificationBannerConfig({
        header: isRisked
          ? ppricingScenarioLocales('offer-error-toast-header')
          : ppricingScenarioLocales('notification-offer-header-failure'),
        subHeader: ppricingScenarioLocales('add-pricing-scenario-notification-error-body')
      });
      setIsPricingScenarios(false);
      return false;
    }
  };

  const lockAndUnlock = async () => {
    setIsLockAndUnlock(false);
    const payload = {
      masterPoolAccountNumber: Number(masterPoolCmhcAccountNumber),
      scenarioNumber: risk?.scenarioNumber
    };
    const isLocked = risk?.lockIndicator === IsLockedIndicator.IsLockedIndicator;

    try {
      const result = await lockUnlock({
        authenticationContext,
        payload
      });

      if (result) {
        setIsOfferToastNotification(true);

        setToastNotificationConfig({
          header: isLocked
            ? ppricingScenarioLocales('unlock-in-progress')
            : ppricingScenarioLocales('lock-in-progress'),
          subHeader: ''
        });
      }
    } catch (error) {
      if (isWorkflowConflictError(error)) {
        showWorkflowInProgressToast();
        return;
      }
      setErrorNotification(true);
      setNotificationBannerConfig({
        header: isLocked
          ? ppricingScenarioLocales('fail-to-unlock-master-pool')
          : ppricingScenarioLocales('fail-to-lock-master-pool'),
        subHeader: ppricingScenarioLocales('add-pricing-scenario-notification-error-body')
      });
    }
  };
  return (
    <>
      <Helmet>
        <title>{t('Pricing Scenario')}</title>
      </Helmet>
      <HbtBreadcrumbComponent
        {...ppricingScenarioLocales('breadcrumb', {
          returnObjects: true,
          searchParams
        })}
        ids={{
          accountNum: masterPoolCmhcAccountNumber,
          masterPoolAccountStatus: pricingScenario?.masterPoolStatusCode ?? ''
        }}
        skippedHrefs={skippedHrefs}
      />
      <Box className={styles.pricingScenarioWrapper}>
        <div className={styles.notificationBanner}>
          <NotificationBanner
            showNotificationBanner={errorNotification}
            onCloseNotificationBanner={() => setErrorNotification(!errorNotification)}
            headerText={{ value: notificationBannerConfig.header }}
            message={{ value: notificationBannerConfig.subHeader }}
            variant={NotificationBannerType.ERROR}
          />
        </div>
        <Box className={styles.pricingScenarioContainer}>
          <Stack
            className={styles.titleContainer}
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
          >
            <Stack>
              <Stack
                direction="row"
                className={styles.masterPoolNumberContainer}
                alignItems="center"
              >
                <Typography className={styles.masterPoolNumberText} variant="h4">
                  {t('PI-Master-Pool-Premiums-Taxes-Flyout-Masterpool-Number')}{' '}
                  {masterPoolCmhcAccountNumber}
                </Typography>
                {masterPoolAccountStatusCode && (
                  <LoanStatusPill
                    status={masterPoolAccountStatusCode as keyof typeof loanStatusMapping}
                    label={getLoanStatusLabel(
                      masterPoolAccountStatusCode as keyof typeof loanStatusMapping,
                      t
                    )}
                  />
                )}
              </Stack>
              <Stack
                direction="column"
                alignItems="flex-start"
                className={styles.headerTextContainer}
              >
                <Typography variant="h1" className={styles.headerMainText}>
                  {t('Scenarios')}
                </Typography>
              </Stack>
            </Stack>
            <Box display="flex" alignItems="center" gap={2} marginTop={{ xs: 2, md: 0 }}>
              {isRisked && (
                <Button
                  type="button"
                  className={styles.addScenarioButton}
                  onClick={() => toggleAddScenarioPricingScenarioDrawer(true)}
                  name="actions"
                  variant={ButtonVariant.SECONDARY}
                  text={{ value: t('Add-Scenario') }}
                  ariaText={{ value: t('Add-Scenario') }}
                />
              )}
              {status && (
                <Button
                  type="button"
                  onClick={() => setIsPricingScenarios(true)}
                  name="actions"
                  variant={ButtonVariant.PRIMARY}
                  text={{
                    value: isRisked
                      ? ppricingScenarioLocales('offer')
                      : ppricingScenarioLocales('remove-offer')
                  }}
                  ariaText={{
                    value: isRisked
                      ? ppricingScenarioLocales('offer')
                      : ppricingScenarioLocales('remove-offer')
                  }}
                  className={styles.offerScenarioButton}
                />
              )}
            </Box>
            <PricingScenarios
              id={'masterpool-reverse-finalization'}
              isOpen={isPricingScenarios}
              onClose={() => setIsPricingScenarios(false)}
              onClick={() => offerScenarioConfiguration()}
              title={
                isRisked
                  ? ppricingScenarioLocales('confirm-offer-header')
                  : ppricingScenarioLocales('remove-offer-confirmation')
              }
              primary={
                isRisked
                  ? ppricingScenarioLocales('offer')
                  : ppricingScenarioLocales('remove-offer')
              }
              secondary={ppricingScenarioLocales('cancel-button')}
            >
              <Box>
                <p className={styles.modalSubHeader}>
                  {isRisked
                    ? ppricingScenarioLocales('confirm-offer-sub-header')
                    : ppricingScenarioLocales('remove-offer-body')}
                </p>
                {isRisked && <OfferTable data={pricingModalData} />}
              </Box>
            </PricingScenarios>
            <PricingScenarios
              id={'lock-and-unlock'}
              isOpen={isLockAndUnlock}
              onClose={() => setIsLockAndUnlock(false)}
              onClick={() => lockAndUnlock()}
              title={
                risk?.lockIndicator === IsLockedIndicator.IsLockedIndicator
                  ? ppricingScenarioLocales('modal-lock-unlock-confirmation')
                  : ppricingScenarioLocales('modal-lock-confirmation')
              }
              primary={
                risk?.lockIndicator === IsLockedIndicator.IsLockedIndicator
                  ? ppricingScenarioLocales('unlock')
                  : ppricingScenarioLocales('lock')
              }
              secondary={ppricingScenarioLocales('cancel-button')}
            >
              <Box>
                <p className={styles.modalSubHeader}>
                  {risk?.lockIndicator === IsLockedIndicator.IsLockedIndicator
                    ? ppricingScenarioLocales('unlock-body-content')
                    : ppricingScenarioLocales('modal-lock-confirmation-body')}
                </p>
              </Box>
            </PricingScenarios>
          </Stack>
          <div className={styles.dataTableContainer}>
            {transposedData.length === 0 && !isFetchingPricingScenario ? (
              <Box className={styles.emptyDataContainer}>
                {ppricingScenarioLocales('MasterPool-Pricing-Scenario-Empty-Data-Error-Header')}
              </Box>
            ) : (
              !isFetchingPricingScenario && (
                <MRTDataTable<ScenarioData>
                  data={transposedData || []}
                  key={tableKey}
                  columns={columns}
                  enableExpandAll={true}
                  enableSorting={false}
                  enableExpanding={false}
                  enableColumnActions={false}
                  enableColumnPinning={true}
                  paginateExpandedRows={false}
                  filterFromLeafRows={false}
                  getSubRows={(row) => row.subRows}
                  initialState={{ expanded: true, columnPinning: { left: ['rowHeader'] } }}
                  enableBottomToolbar={false}
                  localization={mrtCustomTranslation}
                  memoMode="cells"
                  columnResizeMode="onChange"
                  muiTableBodyRowProps={({ row }) => ({
                    className: row.depth === 0 ? 'row-header-depth-0' : 'row-header-depth-1',
                    sx: {
                      td: rowStyle(row.depth, { ignoreBg: true })
                    }
                  })}
                  muiTableBodyCellProps={({ column, row }) => {
                    const isPinnedColumn = Boolean(column.getIsPinned());
                    const isLockedColumn = column.columnDef?.lockIndicator === '1';
                    const rowHeaderKey = row?.original?.rowHeaderKey;
                    const shouldHighlightPinnedHeader = Boolean(
                      isPinnedColumn &&
                      row.depth === 0 &&
                      rowHeaderKey &&
                      PINNED_ROW_HEADER_KEYS.has(rowHeaderKey)
                    );
                    const isRowHeaderColumn = column.id === 'rowHeader';
                    const rowHeaderDepthClass =
                      row.depth === 0 ? 'row-header-cell-root' : 'row-header-cell-child';

                    let backgroundColor: string | undefined;
                    if (shouldHighlightPinnedHeader) {
                      backgroundColor = `${PINNED_COLUMN_BACKGROUND} !important`;
                    } else if (isLockedColumn) {
                      backgroundColor = '#E1E4E7 !important';
                    }

                    const classNames = [
                      isPinnedColumn ? 'pinned-column-cell' : undefined,
                      isRowHeaderColumn ? 'row-header-cell' : undefined,
                      isRowHeaderColumn ? rowHeaderDepthClass : undefined
                    ].filter(Boolean);

                    return {
                      className: classNames.length ? classNames.join(' ') : undefined,
                      sx: {
                        backgroundColor
                      }
                    };
                  }}
                />
              )
            )}
          </div>
          <HbtMasterPoolPricingScenariosFlyout
            key={`addScenarioFlyout${selectedScenarioNumber ?? ''}`}
            openPricingScenarioFlyout={openAddScenarioPricingScenarioFlyout}
            togglePricingScenarioDrawer={toggleAddScenarioPricingScenarioDrawer}
            toggleToastNotification={toggleAddScenarioToastNotification}
            setNotificationBannerConfig={setNotificationBannerConfig}
            setErrorNotification={setErrorNotification}
            isNewScenario={true}
          />
          <ToastNotification
            dismissMode="autoAndActionable"
            open={addScenarioToastOpen}
            header={{
              value: ppricingScenarioLocales('add-offer-header')
            }}
            variant={ToastNotificationVariant.INFO}
            icon={'icon_check_circle'}
            onClose={() => setAddScenarioToastOpen(false)}
            subHeader={{
              value: ppricingScenarioLocales('add-toast-body')
            }}
          />
          <div className={styles.offerToastNotification}>
            <ToastNotification
              dismissMode={'actionable'}
              open={isOfferToastNotification}
              header={{ value: toastNotificationConfig.header }}
              variant={ToastNotificationVariant.INFO}
              icon={'icon_check_circle'}
              onClose={() => setIsOfferToastNotification(false)}
              subHeader={{
                value:
                  toastNotificationConfig.subHeader ||
                  ppricingScenarioLocales('offer-toast-sub-header')
              }}
            />
          </div>
          <ToastNotification
            dismissMode="autoAndActionable"
            open={modifyScenarioToastOpen}
            header={{ value: ppricingScenarioLocales('modify-toast-header') }}
            variant={ToastNotificationVariant.INFO}
            icon={'icon_check_circle'}
            onClose={() => setModifyScenarioToastOpen(false)}
            subHeader={{ value: ppricingScenarioLocales('modify-toast-body') }}
          />
          <HbtMasterPoolPricingScenariosFlyout
            key={`modifyScenarioFlyout${selectedScenarioNumber ?? ''}`}
            openPricingScenarioFlyout={openModifyScenarioPricingScenarioFlyout}
            togglePricingScenarioDrawer={toggleModifyScenarioPricingScenarioDrawer}
            toggleToastNotification={toggleModifyScenarioToastNotification}
            scenarioNumber={selectedScenarioNumber}
            setNotificationBannerConfig={setNotificationBannerConfig}
            setErrorNotification={setErrorNotification}
          />
        </Box>
      </Box>
    </>
  );
};

export default HbtMasterPoolPricingScenarios;
